import React, { createContext, useContext, useMemo } from 'react';
import type { RoleKey, ActionKey, ResourceKey } from '../../config/permissions';
import type { User } from '../../types/services/user';
import { useAuth } from '../auth/AuthContext';
import { can as canUtil, hasRole as hasRoleUtil, hasAttribute as hasAttributeUtil } from '../../utils/permissions';

type PermissionContextType = {
  currentUser: User | null;
  can: (action: ActionKey, resource: ResourceKey, context?: Record<string, any>) => boolean;
  hasRole: (role: RoleKey) => boolean;
  hasAttribute: (key: string, value?: any) => boolean;
};

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user: authUser } = useAuth();

  const currentUser: User | null = useMemo(() => {
    if (!authUser) return null;
    return authUser;
  }, [authUser]);

  const value: PermissionContextType = {
    currentUser,
    can: (action, resource, context) => canUtil(currentUser, action, resource, context),
    hasRole: (role) => hasRoleUtil(currentUser, role),
    hasAttribute: (key, value) => hasAttributeUtil(currentUser, key, value),
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermission = (): PermissionContextType => {
  const ctx = useContext(PermissionContext);
  if (!ctx) throw new Error('usePermission debe usarse dentro de PermissionProvider');
  return ctx;
};